import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import RSEInteractiveBookCTA from '../components/RSEInteractiveBookCTA';
import VittaRSEPathways from '../components/VittaRSEPathways';

const RSEStudyGuide = () => {
  // Book and Course data
  const studyMaterials = {
    book: {
      title: "Printed RSE Study Guide",
      subtitle: "Complete 780 Page Book",
      description: "Comprehensive study guide for the CIRO Retail Securities Exam using the Financial Doctor Methodology.",
      price: "299.99",
      link: "https://www.amazon.ca/dp/B0G3L3TCJR"
    },
    interactiveBook: {
      title: "RSE Interactive Study Guide",
      description: "Master the RSE exam with interactive flashcards, quizzes, and visual learning tools across all 9 elements.",
      price: "49.99",
      link: "/books/rse-interactive"
    },
    course: {
      title: "RSE Exam Prep Course",
      price: "29.99",
      link: "https://vittafinance.mykajabi.com/offers/2kzQbJim"
    }
  };

  const learningPath = [
    {
      phase: "Phase 1: Getting Started",
      description: "Understand the RSE exam basics and create your study plan",
      duration: "Week 1",
      articles: [
        {
          title: "What is the CIRO Exam? Complete Beginner's Guide",
          slug: "what-is-ciro-exam-beginners-guide",
          description: "Everything you need to know about CIRO exams"
        },
        {
          title: "Pass the RSE Exam: Strategy & Syllabus Guide",
          slug: "rse-exam-strategy-guide",
          description: "Complete RSE exam breakdown with all 9 elements"
        },
        {
          title: "RSE vs ISE: Which Exam Should You Take?",
          slug: "rse-vs-ise-exam-comparison",
          description: "Compare retail vs institutional career paths"
        },
        {
          title: "CIRO Exam Study Schedule: 30, 60, 90 Day Plans",
          slug: "ciro-exam-study-schedule-plan",
          description: "Choose the right study timeline for RSE"
        }
      ]
    },
    {
      phase: "Phase 2: Element 1 - KYC & Suitability (23%)",
      description: "Master the largest exam section with 27 questions",
      duration: "Week 2-3",
      articles: [
        {
          title: "RSE Element 1: KYC & Suitability Complete Guide",
          slug: "rse-exam-kyc-suitability-guide",
          description: "CRM2, Risk Tolerance vs Capacity, KYC triggers, TCP"
        }
      ],
      highlight: true,
      topics: [
        "Client Relationship Model (CRM2) disclosures",
        "Risk Tolerance vs. Risk Capacity - The Golden Rule",
        "KYC Triggers and update requirements",
        "Trusted Contact Person (TCP) and Temporary Holds",
        "Suitability Determination: The 3-Step Match",
        "Accredited Investors and Permitted Clients",
        "Anti-Money Laundering (AML) and PEPs"
      ]
    },
    {
      phase: "Phase 3: Element 2 - Fixed Income (8%)",
      description: "Learn bond pricing, duration, and yield calculations",
      duration: "Week 3",
      articles: [
        {
          title: "RSE Fixed Income Guide: Bonds, Duration & Yields",
          slug: "rse-exam-fixed-income-bonds-guide",
          description: "Bond pricing mechanics, GOC bonds, preferred shares"
        }
      ],
      topics: [
        "Bond pricing - The Teeter-Totter (inverse relationship)",
        "Current Yield vs. Yield to Maturity (YTM)",
        "Duration and interest rate sensitivity",
        "Government of Canada Bonds and T-Bills",
        "Corporate bonds: Mortgage vs. Debenture",
        "Preferred Shares and Dividend Tax Credit"
      ]
    },
    {
      phase: "Phase 4: Element 3 - Equities (10%)",
      description: "Master stocks, order types, and trading mechanics",
      duration: "Week 4",
      articles: [
        {
          title: "RSE Equities Guide: Stocks, Orders & Short Selling",
          slug: "rse-exam-equities-stocks-guide",
          description: "Common shares, order types, margin accounts"
        }
      ],
      topics: [
        "Common shares: Voting rights and residual claim",
        "Order Types: Market, Limit, Stop-Loss, Stop-Limit",
        "Short Selling mechanics and unlimited risk",
        "Margin Accounts and Margin Calls",
        "Rights vs. Warrants",
        "Growth vs. Value vs. Cyclical vs. Defensive stocks"
      ]
    },
    {
      phase: "Phase 5: Element 4 - Securities Analysis (12%)",
      description: "Fundamental analysis, ratios, and economic indicators",
      duration: "Week 4-5",
      articles: [
        {
          title: "RSE Securities Analysis: Ratios & Indicators",
          slug: "rse-exam-securities-analysis-guide",
          description: "P/E, ROE, economic indicators, technical analysis"
        }
      ],
      topics: [
        "Top-Down Analysis: Economy → Industry → Company",
        "Fiscal vs. Monetary Policy",
        "Leading, Coincident, and Lagging Indicators",
        "Financial Ratios: P/E, ROE, Current Ratio, Quick Ratio",
        "Technical Analysis: Support, Resistance, Moving Averages",
        "Efficient Market Hypothesis (EMH)"
      ]
    },
    {
      phase: "Phase 6: Element 5 - Managed Products (13%)",
      description: "Mutual funds, ETFs, segregated funds, and alternatives",
      duration: "Week 5",
      articles: [
        {
          title: "RSE Managed Products: Mutual Funds, ETFs, Seg Funds",
          slug: "rse-exam-managed-products-guide",
          description: "NAVPU, MER, open-end vs closed-end, Liquid Alts"
        }
      ],
      topics: [
        "Mutual Fund NAVPU calculation",
        "Open-End vs. Closed-End Funds",
        "MER (Management Expense Ratio) impact",
        "ETF structure and Physical vs. Synthetic",
        "Leveraged & Inverse ETFs - Daily Reset Risk",
        "Segregated Funds: Guarantees and Creditor Protection",
        "Alternative Investments and Liquid Alts"
      ]
    },
    {
      phase: "Phase 7: Element 6 - Portfolio Construction (11%)",
      description: "Asset allocation, risk metrics, and registered accounts",
      duration: "Week 5-6",
      articles: [
        {
          title: "RSE Portfolio Construction: Asset Allocation & Risk",
          slug: "rse-exam-portfolio-construction-guide",
          description: "Standard Deviation, Beta, Sharpe Ratio, RRSP, TFSA"
        }
      ],
      topics: [
        "Strategic vs. Tactical Asset Allocation",
        "Correlation and diversification benefits",
        "Standard Deviation (Total Risk) vs. Beta (Market Risk)",
        "Sharpe Ratio and Alpha",
        "RRSP, TFSA, RESP, RRIF, FHSA rules",
        "Tax efficiency: Capital Gains vs. Dividends vs. Interest"
      ]
    },
    {
      phase: "Phase 8: Element 7 - Investment Recommendations (12%)",
      description: "Suitability framework, disclosures, and vulnerable clients",
      duration: "Week 6",
      articles: [
        {
          title: "RSE Investment Recommendations: Suitability & Best Interest",
          slug: "rse-exam-investment-recommendations-guide",
          description: "Net Benefit rule, leverage, concentration risk"
        }
      ],
      topics: [
        "Product Suitability matching process",
        "Cost, Risk, and Conflict Disclosure requirements",
        "Dealing with Senior and Vulnerable Clients",
        "Leverage Suitability criteria",
        "Switching and the Net Benefit Rule",
        "ESG Preferences and Behavioral Biases"
      ]
    },
    {
      phase: "Phase 9: Element 8 - Execution & Market Integrity (6%)",
      description: "Best execution, settlement rules, and prohibited practices",
      duration: "Week 6-7",
      articles: [
        {
          title: "RSE Execution & Market Integrity Guide",
          slug: "rse-exam-execution-market-integrity-guide",
          description: "T+1 settlement, front running, market manipulation"
        }
      ],
      topics: [
        "Best Execution Obligation",
        "Client Priority Rule",
        "T+1 Settlement Rules",
        "Prohibited Practices: Front Running, Tipping, Wash Trading",
        "Short Selling Rules and requirements",
        "Error Correction procedures"
      ]
    },
    {
      phase: "Phase 10: Element 9 - Monitoring & Client Relationships (21%)",
      description: "Performance reporting, complaints, and ongoing compliance",
      duration: "Week 7",
      articles: [
        {
          title: "RSE Monitoring & Client Relationships Guide",
          slug: "rse-exam-monitoring-client-relationships-guide",
          description: "MWRR vs TWRR, OBSI, complaint handling"
        }
      ],
      highlight: true,
      topics: [
        "MWRR (Client statements) vs. TWRR (Fund marketing)",
        "Annual Performance and Fee Reporting (CRM2)",
        "Complaint Handling: 5-day acknowledgment, 90-day response",
        "OBSI ($350,000 limit) vs. Arbitration ($500,000 limit)",
        "Record Keeping (7 years) and Electronic Communications",
        "Continuing Education requirements"
      ]
    },
    {
      phase: "Phase 11: Math & Final Review",
      description: "Essential calculations and exam preparation",
      duration: "Week 7-8",
      articles: [
        {
          title: "RSE Exam Math & Calculations Guide",
          slug: "rse-exam-math-calculations-guide",
          description: "Margin calls, yields, ratios, portfolio metrics"
        },
        {
          title: "Free CIRO Practice Questions",
          slug: "free-ciro-practice-questions",
          description: "Test your knowledge before exam day"
        }
      ],
      topics: [
        "Margin Call Trigger Price formula",
        "Current Yield and YTM calculations",
        "P/E Ratio, ROE, Current Ratio, Quick Ratio",
        "Sharpe Ratio and CAPM formula",
        "RRSP and RESP contribution limits",
        "Rule of 72 for quick doubling estimates"
      ]
    }
  ];

  const additionalResources = [
    {
      title: "CIRO Exam Requirements & Eligibility",
      slug: "ciro-exam-requirements-eligibility",
      description: "Registration and prerequisites"
    },
    {
      title: "How to Register for CIRO Exams",
      slug: "how-to-register-ciro-exam",
      description: "Step-by-step registration guide"
    },
    {
      title: "CIRO Exam Cost & Fees Guide",
      slug: "ciro-exam-cost-fees-guide",
      description: "Complete pricing breakdown"
    },
    {
      title: "CIRO Exam Pass Rates & Difficulty",
      slug: "ciro-exam-pass-rates-difficulty",
      description: "What to expect on the exam"
    },
    {
      title: "Investment Career Path in Canada",
      slug: "investment-career-path-canada",
      description: "From entry to senior advisor"
    },
    {
      title: "Financial Services Career Roadmap",
      slug: "financial-services-career-roadmap-blueprint",
      description: "Complete career blueprint"
    }
  ];

  return (
    <>
      <SEO 
        title="RSE Exam Study Guide | Free Learning Path & All 9 Elements | Vitta Finance Academy"
        description="Master the Retail Securities Exam (RSE) with our structured 8-week study guide covering all 9 elements. Free learning path with KYC, fixed income, equities, managed products, portfolio construction, and more. Pass RSE on your first attempt."
        keywords="RSE study guide, RSE exam preparation, Retail Securities Exam learning path, RSE exam topics, RSE free resources, RSE element 1 KYC, RSE exam strategy, CIRO RSE guide"
      />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        padding: '80px 20px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#d4af37',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            FREE STUDY RESOURCE
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            RSE Exam Study Guide
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.7'
          }}>
            Your complete 8-week learning path to pass the Retail Securities Exam. 
            Master all 9 elements from KYC & Suitability to Portfolio Construction with our structured guide.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/rse-exam"
              data-testid="rse-course-btn"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              View RSE Course
            </Link>
            <Link 
              to="/books"
              data-testid="study-materials-btn"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Get Study Materials
            </Link>
          </div>
        </div>
      </section>

      {/* Exam Overview */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '40px' }}>
            RSE Exam at a Glance
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {[
              { label: 'Questions', value: '120', sublabel: 'Multiple Choice' },
              { label: 'Duration', value: '3 Hours', sublabel: 'Computer-Based' },
              { label: 'Passing Score', value: 'Set by CIRO', sublabel: 'Per Sitting' },
              { label: 'Study Time', value: '6-8 Weeks', sublabel: 'Recommended' },
              { label: 'Elements', value: '9 Topics', sublabel: 'Comprehensive' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e3a8a' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginTop: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '3px' }}>{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Element Weightings */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            RSE Exam Element Weightings
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            {[
              { element: 'Element 1', name: 'KYC & Suitability', weight: '23%', questions: '~27', priority: 'high' },
              { element: 'Element 9', name: 'Monitoring & Relationships', weight: '21%', questions: '~26', priority: 'high' },
              { element: 'Element 5', name: 'Managed Products', weight: '13%', questions: '~16', priority: 'medium' },
              { element: 'Element 4', name: 'Securities Analysis', weight: '12%', questions: '~14', priority: 'medium' },
              { element: 'Element 7', name: 'Recommendations', weight: '12%', questions: '~14', priority: 'medium' },
              { element: 'Element 6', name: 'Portfolio Construction', weight: '11%', questions: '~13', priority: 'medium' },
              { element: 'Element 3', name: 'Equities', weight: '10%', questions: '~12', priority: 'low' },
              { element: 'Element 2', name: 'Fixed Income', weight: '8%', questions: '~10', priority: 'low' },
              { element: 'Element 8', name: 'Execution', weight: '6%', questions: '~7', priority: 'low' }
            ].map((el, index) => (
              <div key={index} style={{
                background: el.priority === 'high' ? 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)' : 'white',
                color: el.priority === 'high' ? 'white' : '#1e3a8a',
                padding: '15px',
                borderRadius: '10px',
                border: el.priority === 'high' ? 'none' : '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{el.weight}</div>
                <div style={{ fontSize: '0.8rem', opacity: el.priority === 'high' ? 0.8 : 0.6, marginTop: '2px' }}>{el.questions} questions</div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  marginTop: '8px',
                  borderTop: `1px solid ${el.priority === 'high' ? 'rgba(255,255,255,0.2)' : '#e5e7eb'}`,
                  paddingTop: '8px'
                }}>
                  {el.name}
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '20px', fontSize: '0.9rem' }}>
            Focus on Elements 1 and 9 - they account for 44% of the exam
          </p>
        </div>
      </section>

      {/* Learning Path */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '15px' }}>
            Structured Learning Path
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
            Follow this 11-phase study plan to systematically master all RSE exam content
          </p>

          {learningPath.map((phase, phaseIndex) => (
            <div key={phaseIndex} style={{ marginBottom: '35px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: phase.highlight 
                    ? 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)'
                    : 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                  color: phase.highlight ? '#1e3a8a' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {phaseIndex + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', margin: '0 0 3px' }}>{phase.phase}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                    {phase.description} • <span style={{ color: '#d4af37', fontWeight: '600' }}>{phase.duration}</span>
                  </p>
                </div>
              </div>

              <div style={{
                marginLeft: '22px',
                borderLeft: phaseIndex < learningPath.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingLeft: '35px',
                paddingBottom: phaseIndex < learningPath.length - 1 ? '15px' : '0'
              }}>
                {/* Articles */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: phase.articles.length > 1 ? 'repeat(auto-fit, minmax(250px, 1fr))' : '1fr',
                  gap: '12px',
                  marginBottom: phase.topics ? '15px' : '0'
                }}>
                  {phase.articles.map((article, articleIndex) => (
                    <Link 
                      key={articleIndex}
                      to={`/blog/${article.slug}`}
                      data-testid={`article-${article.slug}`}
                      style={{
                        background: 'white',
                        padding: '16px 18px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        border: phase.highlight ? '2px solid #d4af37' : '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#1e3a8a';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = phase.highlight ? '#d4af37' : '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <h4 style={{ 
                        fontSize: '0.95rem', 
                        color: '#1e3a8a', 
                        margin: '0 0 6px',
                        lineHeight: '1.4'
                      }}>
                        {article.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.8rem', 
                        color: '#6b7280', 
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        {article.description}
                      </p>
                    </Link>
                  ))}
                </div>

                {/* Topics covered */}
                {phase.topics && (
                  <div style={{
                    background: 'white',
                    padding: '15px 18px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '10px', fontWeight: '600' }}>
                      Key Topics Covered:
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {phase.topics.map((topic, topicIndex) => (
                        <span key={topicIndex} style={{
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Study Pathways */}
      <VittaRSEPathways />

      {/* Additional Resources */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Additional Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
          }}>
            {additionalResources.map((resource, index) => (
              <Link 
                key={index}
                to={`/blog/${resource.slug}`}
                style={{
                  background: 'white',
                  padding: '18px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#d4af37'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#d4af37',
                  flexShrink: 0
                }} />
                <div>
                  <div style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: '600' }}>{resource.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{resource.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Study Resources */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                📚
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#1e3a8a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Interactive Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #8b5cf6',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700'
              }}>NEW</div>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                ⚡
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.interactiveBook.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.interactiveBook.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.interactiveBook.price}
                </span>
              </div>
              <Link 
                to={studyMaterials.interactiveBook.link}
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Explore Interactive Book
              </Link>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #1e3a8a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: '#1e3a8a',
                fontSize: '1.5rem'
              }}>
                🎓
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Video lessons, AI tutor, practice questions, mock exams, and expert instruction.
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>/month</span>
              </div>
              <a 
                href={studyMaterials.course.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#d4af37',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Enroll Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Book CTA */}
      <RSEInteractiveBookCTA />

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Ready to Start Your RSE Journey?</h2>
          <p style={{ opacity: '0.9', marginBottom: '30px', fontSize: '1.1rem' }}>
            Get comprehensive prep with our RSE course featuring video lessons, practice questions, and full mock exams.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/rse-exam"
              data-testid="cta-rse-course"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}
            >
              Explore RSE Course - $29.99/month
            </Link>
            <Link 
              to="/cire-study-guide"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              View CIRE Study Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default RSEStudyGuide;
